/**
 * Custom product page behaviours.
 *
 * Safe to load on every product page — each block below gates itself on the
 * template suffix it applies to.
 */
(function () {
  'use strict';

  /**
   * Returns the current product template suffix, e.g. "bundle-no-product-form"
   * for templates/product.bundle-no-product-form.json.
   *
   * Read from the data attribute on this script tag, with window.Theme as a
   * fallback for when the file is loaded some other way.
   */
  function getTemplateSuffix() {
    const script = document.currentScript;
    let suffix = script && script.getAttribute('data-template-suffix');

    if (suffix == null && window.Theme) {
      suffix = window.Theme.templateSuffix;
    }

    return (suffix || '').trim();
  }

  const templateSuffix = getTemplateSuffix();

  function onReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  /**
   * Returns the elements matching `selector` inside `container` that belong to
   * `container` itself rather than to a nested one of the same kind.
   */
  function findOwn(container, selector, containerSelector) {
    const matches = container.querySelectorAll(selector);

    return Array.prototype.filter.call(matches, function (match) {
      return match.closest(containerSelector) === container;
    });
  }

  /* ----------------------------------------------------------------------
   * templates/product.bundle-no-product-form.json
   *
   * Tweaks to the Unlimited Bundles widget. Its markup is injected after page
   * load and redrawn as the shopper changes their selection, so every tweak
   * below is written as an idempotent pass over the current DOM and driven by
   * the single shared observer at the bottom.
   * ------------------------------------------------------------------- */

  const BUNDLE_WIDGET = {
    buttonText: '.rbu-sb-btn__text',
    tierProduct: '.rbu-sb-tier-product-container',
    tierProductTitleLink: '.rbu-sb-tier-product-title-link',
    tierProductMultipleVariants: '.rbu-sb-tier-product-variants__multiple',
    tierVariantWrapper: '.rbu-sb-tier-variant-wrapper'
  };

  const WIDGET_TIMEOUT_MS = 20000;

  /**
   * The widget renders its own button label. Append the product price to it,
   * so the button reads "Add to cart - $49.00".
   */
  function appendPriceToBundleButton() {
    const BUTTON_LABEL_PATTERN = /^add to cart$/i;
    const PRICE_SELECTOR = '[data-product-pricing] [data-price]';
    const FALLBACK_PRICE_SELECTOR = '[data-price]';

    const buttonTexts = document.querySelectorAll(BUNDLE_WIDGET.buttonText);
    if (!buttonTexts.length) return;

    const priceElement =
      document.querySelector(PRICE_SELECTOR) ||
      document.querySelector(FALLBACK_PRICE_SELECTOR);
    if (!priceElement) return;

    const price = (priceElement.textContent || '').trim();
    if (!price) return;

    Array.prototype.forEach.call(buttonTexts, function (buttonText) {
      // Remember the app's own label so re-runs append to it rather than
      // stacking another price onto our own output.
      let label = buttonText.getAttribute('data-label');

      if (label === null) {
        label = (buttonText.textContent || '').replace(/\s+/g, ' ').trim();

        // Leave any other label the app renders alone.
        if (!BUTTON_LABEL_PATTERN.test(label)) return;

        buttonText.setAttribute('data-label', label);
      }

      const decorated = label + ' - ' + price;

      if (buttonText.textContent !== decorated) {
        buttonText.textContent = decorated;
      }
    });
  }

  /**
   * A tier product with more than one variant selector needs its quantity made
   * obvious, so prefix the title with the number of selectors — "2x - Product
   * name". Each tier product container carries its own title and selectors, so
   * every container is counted and prefixed independently.
   */
  function prefixTierProductTitlesWithCount() {
    const containers = document.querySelectorAll(BUNDLE_WIDGET.tierProduct);
    if (!containers.length) return;

    Array.prototype.forEach.call(containers, function (container) {
      const titleLink = findOwn(
        container,
        BUNDLE_WIDGET.tierProductTitleLink,
        BUNDLE_WIDGET.tierProduct
      )[0];
      if (!titleLink) return;

      const variants = findOwn(
        container,
        BUNDLE_WIDGET.tierProductMultipleVariants,
        BUNDLE_WIDGET.tierProduct
      )[0];
      if (!variants) return;

      const count = variants.querySelectorAll(BUNDLE_WIDGET.tierVariantWrapper).length;
      if (!count) return;

      // Remember the app's own title so re-runs replace the prefix rather than
      // stacking another one onto our own output.
      let title = titleLink.getAttribute('data-title');

      if (title === null) {
        title = (titleLink.textContent || '').replace(/\s+/g, ' ').trim();
        if (!title) return;
        titleLink.setAttribute('data-title', title);
      }

      const decorated = count + 'x ' + title;

      if (titleLink.textContent !== decorated) {
        titleLink.textContent = decorated;
      }
    });
  }

  /**
   * Runs every tweak now and again on each DOM change, so they survive the app
   * injecting and redrawing its widget. If none of the widget markup has shown
   * up within the timeout the app isn't on this page, and we stop watching.
   */
  function watchBundleWidget() {
    const tweaks = [appendPriceToBundleButton, prefixTierProductTitlesWithCount];
    const widgetSelectors = [BUNDLE_WIDGET.buttonText, BUNDLE_WIDGET.tierProduct];

    function runTweaks() {
      tweaks.forEach(function (tweak) {
        tweak();
      });
    }

    runTweaks();

    const observer = new MutationObserver(runTweaks);
    observer.observe(document.body, { childList: true, subtree: true });

    window.setTimeout(function () {
      const rendered = widgetSelectors.some(function (selector) {
        return document.querySelector(selector) !== null;
      });

      if (!rendered) observer.disconnect();
    }, WIDGET_TIMEOUT_MS);
  }

  if (templateSuffix === 'bundle-no-product-form') {
    onReady(watchBundleWidget);
  }
})();
